import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/auth';
import { hojeBrasilia } from '../utils/dataBrasilia';
import './Planos.css';

const COR = 'rgb(235, 133, 0)';
const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const campo = (o, nome) => o?.[nome] ?? o?.[nome.charAt(0).toUpperCase() + nome.slice(1)];

// "2026-10-29T14:05:00" → { dia: '29/10/2026', hora: '14:05' } (já está em horário de Brasília)
const partes = (dataHora) => {
  const m = String(dataHora || '').match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!m) return { dia: '—', hora: '', chave: '' };
  return { dia: `${m[3]}/${m[2]}/${m[1]}`, hora: m[4] ? `${m[4]}:${m[5]}` : '', chave: `${m[1]}-${m[2]}-${m[3]}` };
};

// Extrato financeiro: tudo o que entrou (pagamentos pelo sistema e pagamentos externos), com data e hora exatas
// e a venda de onde veio, como um extrato de banco. No topo, o resumo das vendas aprovadas (externo × interno).
export default function ExtratoFinanceiro() {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState('mes'); // 'tudo' | 'mes' | '30' | 'personalizado'
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  const intervalo = useMemo(() => {
    const hoje = hojeBrasilia();
    if (periodo === 'tudo') return { inicio: '', fim: '' };
    if (periodo === 'mes') return { inicio: `${hoje.slice(0, 7)}-01`, fim: hoje };
    if (periodo === '30') {
      const d = new Date(`${hoje}T12:00:00`);
      d.setDate(d.getDate() - 29);
      return { inicio: d.toISOString().slice(0, 10), fim: hoje };
    }
    return { inicio, fim };
  }, [periodo, inicio, fim]);

  useEffect(() => { document.title = 'Extrato'; }, []);

  useEffect(() => {
    if (periodo === 'personalizado' && (!inicio || !fim)) return;
    (async () => {
      try {
        setCarregando(true);
        setErro('');
        const qs = new URLSearchParams();
        if (intervalo.inicio) qs.set('inicio', intervalo.inicio);
        if (intervalo.fim) qs.set('fim', intervalo.fim);
        setDados(await apiCall(`Extrato${qs.toString() ? `?${qs}` : ''}`));
      } catch (e) {
        setErro('Não foi possível carregar o extrato.');
      } finally {
        setCarregando(false);
      }
    })();
  }, [intervalo, periodo, inicio, fim]);

  const resumo = campo(dados, 'resumo') || {};
  const lancamentos = campo(dados, 'lancamentos') || [];

  // Agrupa por dia (mais recente primeiro), como extrato de banco
  const dias = useMemo(() => {
    const grupos = [];
    lancamentos.forEach(l => {
      const p = partes(campo(l, 'dataHora'));
      let g = grupos.find(x => x.chave === p.chave);
      if (!g) { g = { chave: p.chave, dia: p.dia, itens: [], total: 0 }; grupos.push(g); }
      g.itens.push({ ...l, hora: p.hora });
      g.total += Number(campo(l, 'valor')) || 0;
    });
    return grupos;
  }, [lancamentos]);

  const cartao = (rotulo, valor, cor, detalhe) => (
    <div style={{ flex: '1 1 180px', background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: 12, color: '#777', fontWeight: 600 }}>{rotulo}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: cor, marginTop: 2 }}>{formatBRL(valor)}</div>
      {detalhe && <div style={{ fontSize: 11, color: '#888', marginTop: 4, lineHeight: 1.4 }}>{detalhe}</div>}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f0f0', padding: '64px 16px 40px', position: 'relative' }}>
      <button type="button" onClick={() => (window.opener ? window.close() : navigate('/sidebar/ContasReceberDropdown'))} title="Fechar o extrato"
        style={{ position: 'fixed', top: 14, left: 14, zIndex: 20, padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#555', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
        ✕ Fechar
      </button>

      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center', color: COR, margin: '0 0 6px' }}>🧾 Extrato Financeiro</h2>
        <p style={{ textAlign: 'center', color: '#666', margin: '0 0 18px', fontSize: 14 }}>
          Tudo o que entrou no caixa, com data, hora e a venda de onde veio.
        </p>

        {/* Período */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', marginBottom: 18 }}>
          {[['mes', 'Este mês'], ['30', 'Últimos 30 dias'], ['tudo', 'Todo o período'], ['personalizado', 'Escolher período']].map(([v, r]) => (
            <button key={v} type="button" onClick={() => setPeriodo(v)}
              style={{ padding: '8px 14px', borderRadius: 6, border: `1px solid ${COR}`, cursor: 'pointer', fontWeight: 600, fontSize: 13, background: periodo === v ? COR : '#fff', color: periodo === v ? '#fff' : COR }}>
              {r}
            </button>
          ))}
          {periodo === 'personalizado' && (
            <>
              <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} style={{ padding: 7, borderRadius: 6, border: '1px solid #ccc' }} />
              <span style={{ color: '#666' }}>até</span>
              <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} style={{ padding: 7, borderRadius: 6, border: '1px solid #ccc' }} />
            </>
          )}
        </div>

        {erro && <div style={{ textAlign: 'center', padding: 14, color: '#b00020', background: '#fdecea', borderRadius: 8, marginBottom: 14 }}>{erro}</div>}
        {carregando && <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>Carregando extrato...</div>}

        {dados && !carregando && (
          <>
            {/* Resumo das vendas aprovadas */}
            <h4 style={{ margin: '0 0 8px', color: '#444' }}>Vendas aprovadas ({campo(resumo, 'quantidadeVendasAprovadas') || 0})</h4>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
              {cartao('Total de vendas aprovadas', campo(resumo, 'totalVendasAprovadas'), '#333')}
              {cartao('Vendas de produtos', campo(resumo, 'totalVendasProdutos'), COR)}
              {cartao('Vendas de serviços', campo(resumo, 'totalVendasServicos'), '#6a1b9a')}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              {cartao('Pagamento externo', campo(resumo, 'totalPagamentoExterno'), '#1565c0', 'Pago fora do sistema (dinheiro, Pix direto, maquininha...).')}
              {cartao('Pagamento interno confirmado', campo(resumo, 'totalPagamentoInternoConfirmado'), '#2e7d32',
                `Pagamentos já confirmados pelo Stripe (link de pagamento), de vendas de produtos e de serviços: ${campo(resumo, 'quantidadePagamentosInternos') || 0} pagamento(s) no período.`)}
            </div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 22 }}>
              O pagamento interno confirmado soma só o que o Stripe já confirmou; venda ainda não paga pelo link não entra. O pagamento externo nunca passa do valor da venda.
            </div>

            {/* Extrato */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <h4 style={{ margin: 0, color: '#444' }}>Extrato de entradas</h4>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#2e7d32' }}>Entradas no período: {formatBRL(campo(resumo, 'totalEntradasPeriodo'))}</span>
            </div>

            {dias.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: '#777', background: '#fff', borderRadius: 10 }}>Nenhuma entrada neste período.</div>
            ) : dias.map(d => (
              <div key={d.chave} style={{ background: '#fff', borderRadius: 10, marginBottom: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', background: '#fafafa', borderBottom: '1px solid #eee', fontSize: 13, fontWeight: 700, color: '#555' }}>
                  <span>{d.dia}</span>
                  <span style={{ color: '#2e7d32' }}>{formatBRL(d.total)}</span>
                </div>
                {d.itens.map((l, i) => {
                  const venda = campo(l, 'venda') || {};
                  const externo = /extern/i.test(campo(l, 'tipo') || '');
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i < d.itens.length - 1 ? '1px solid #f3f3f3' : 'none' }}>
                      <div style={{ width: 44, fontSize: 13, color: '#888', flexShrink: 0 }}>{l.hora}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#333', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: externo ? '#e3f2fd' : '#e8f5e9', color: externo ? '#1565c0' : '#2e7d32' }}>
                            {externo ? 'Pagamento externo' : 'Pagamento interno'}
                          </span>
                          {campo(l, 'forma') && <span style={{ fontWeight: 600, color: '#555' }}>{campo(l, 'forma')}</span>}
                          <span style={{ fontWeight: 600, color: campo(l, 'parcial') ? '#b26a00' : '#555' }}>· {campo(l, 'parcial') ? 'parcial' : 'total'}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Venda #{campo(venda, 'vendaId')} · {campo(venda, 'natureza')}: {campo(venda, 'descricao')}
                          {campo(venda, 'cliente') ? ` · ${campo(venda, 'cliente')}` : ''}
                          {` · valor da venda ${formatBRL(campo(venda, 'valorVenda'))}`}
                        </div>
                      </div>
                      <div style={{ fontWeight: 800, fontSize: 15, color: '#2e7d32', flexShrink: 0 }}>+ {formatBRL(campo(l, 'valor'))}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
